import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout';
import { StatsCard, LoadingState } from '../../../components/admin/shared';
import { Users, FileText, CheckCircle, TrendingUp, Download, Calendar } from 'lucide-react';
import educentialApi from '../../../api/educentialApi';

const ReportsOverview = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    dateFrom: '',
    dateTo: ''
  });

  useEffect(() => {
    loadOverview();
  }, [dateRange]);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.dateFrom) params.append('dateFrom', dateRange.dateFrom);
      if (dateRange.dateTo) params.append('dateTo', dateRange.dateTo);

      const response = await educentialApi.get(`/api/admin/reports/overview?${params.toString()}`);
      setOverview(response.data);
    } catch (error) {
      console.error('Error loading overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    navigate('/admin/reports/export');
  };

  if (loading) {
    return (
      <AdminLayout title="Reports Overview">
        <LoadingState message="Loading analytics..." />
      </AdminLayout>
    );
  }

  const { users, applications, documents } = overview || {};

  return (
    <AdminLayout title="Reports & Analytics">
      <div className="p-6 space-y-6">
        {/* Header with Date Range Filter */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Platform Analytics</h2>
            <p className="text-gray-600 mt-1">Comprehensive overview of platform metrics and performance</p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 bg-white rounded-lg border border-gray-300 px-3 py-2">
              <Calendar className="w-4 h-4 text-gray-500" />
              <input
                type="date"
                value={dateRange.dateFrom}
                onChange={(e) => setDateRange(prev => ({ ...prev, dateFrom: e.target.value }))}
                className="border-0 focus:ring-0 text-sm"
                placeholder="From"
              />
              <span className="text-gray-400">-</span>
              <input
                type="date"
                value={dateRange.dateTo}
                onChange={(e) => setDateRange(prev => ({ ...prev, dateTo: e.target.value }))}
                className="border-0 focus:ring-0 text-sm"
                placeholder="To"
              />
            </div>
            <button
              onClick={handleExport}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* User Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">User Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Total Users"
              value={users?.total || 0}
              icon={Users}
              trend={users?.growthRate > 0 ? 'up' : users?.growthRate < 0 ? 'down' : null}
              trendValue={users?.growthRate ? `${Math.abs(users.growthRate).toFixed(1)}%` : null}
            />
            <StatsCard title="Students" value={users?.students || 0} icon={Users} />
            <StatsCard title="Educators" value={users?.educators || 0} icon={Users} />
            <StatsCard title="Admins" value={users?.admins || 0} icon={Users} />
          </div>
        </div>

        {/* Application Metrics */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Application Metrics</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Total Applications"
              value={applications?.total || 0}
              icon={FileText}
            />
            <StatsCard
              title="This Month"
              value={applications?.thisMonth || 0}
              icon={Calendar}
            />
            <StatsCard
              title="Conversion Rate"
              value={`${applications?.conversionRate?.toFixed(1) || 0}%`}
              icon={TrendingUp}
            />
            <StatsCard
              title="Completed"
              value={applications?.byStatus?.COMPLETED || 0}
              icon={CheckCircle}
            />
          </div>
        </div>

        {/* Application Status Breakdown */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Applications by Status</h3>
          <div className="space-y-3">
            {applications?.byStatus && Object.entries(applications.byStatus).map(([status, count]) => {
              const total = applications.total || 1;
              const percentage = ((count / total) * 100).toFixed(1);

              const colors = {
                DRAFT: 'bg-gray-500',
                SUBMITTED: 'bg-blue-500',
                UNDER_REVIEW: 'bg-yellow-500',
                OFFER_SECURED: 'bg-green-500',
                VISA_IN_PROGRESS: 'bg-purple-500',
                COMPLETED: 'bg-teal-500',
                REJECTED: 'bg-red-500'
              };

              return (
                <div key={status}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">
                      {status.replace(/_/g, ' ')}
                    </span>
                    <span className="text-sm text-gray-600">
                      {count} ({percentage}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${colors[status] || 'bg-gray-500'}`}
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Document Metrics */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Document Review Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">{documents?.total || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Total Documents</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{documents?.pending || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Pending Review</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{documents?.approved || 0}</div>
              <div className="text-sm text-gray-600 mt-1">Approved</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {documents?.approvalRate?.toFixed(1) || 0}%
              </div>
              <div className="text-sm text-gray-600 mt-1">Approval Rate</div>
            </div>
          </div>

          {/* Document Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 flex overflow-hidden">
              {documents?.approved > 0 && (
                <div
                  className="bg-green-500 h-4"
                  style={{ width: `${((documents.approved / documents.total) * 100).toFixed(1)}%` }}
                  title={`Approved: ${documents.approved}`}
                ></div>
              )}
              {documents?.rejected > 0 && (
                <div
                  className="bg-red-500 h-4"
                  style={{ width: `${((documents.rejected / documents.total) * 100).toFixed(1)}%` }}
                  title={`Rejected: ${documents.rejected}`}
                ></div>
              )}
              {documents?.pending > 0 && (
                <div
                  className="bg-yellow-500 h-4"
                  style={{ width: `${((documents.pending / documents.total) * 100).toFixed(1)}%` }}
                  title={`Pending: ${documents.pending}`}
                ></div>
              )}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-600">
              <span>Approved: {((documents?.approved / documents?.total) * 100).toFixed(1)}%</span>
              <span>Rejected: {((documents?.rejected / documents?.total) * 100).toFixed(1)}%</span>
              <span>Pending: {((documents?.pending / documents?.total) * 100).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/admin/reports/services')}
            className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Service Analytics</p>
                <p className="text-sm text-gray-500">Detailed service breakdown</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/reports/users')}
            className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">User Analytics</p>
                <p className="text-sm text-gray-500">Growth and engagement trends</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => navigate('/admin/reports/export')}
            className="p-6 bg-white rounded-lg shadow hover:shadow-md transition-shadow border border-gray-200 text-left"
          >
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Download className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Export Data</p>
                <p className="text-sm text-gray-500">Download reports</p>
              </div>
            </div>
          </button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ReportsOverview;
